// components/UnitPanel/StaminaCore.tsx
import React, { useState, useEffect, useRef } from 'react';
import { BattleBus } from '@/events/Events';
import { StaminaBar } from './StaminaBar';
import './StaminaCore.css';
import { flushSync } from 'react-dom';


interface StaminaCoreProps {
    unitName: string;
    unitId: string;  // ✅ Додай unitId для events
    currentStamina: number;
    maxStamina: number;
    previewCost: number;
}

const BASE_DRAIN_SPEED = 1.2; 
const MIN_DURATION = 0.2;

// StaminaCore.tsx
export const StaminaCore = React.memo(function StaminaCore({ 
    unitName,
    unitId,
    currentStamina, 
    maxStamina, 
    previewCost 
}: StaminaCoreProps) {
    
    const [animatedDrain, setAnimatedDrain] = useState(0);
    const [frozenPreview, setFrozenPreview] = useState(0);
    const [predictedStamina, setPredictedStamina] = useState<number | null>(null);
    const [isDraining, setIsDraining] = useState(false);
    const [drainDuration, setDrainDuration] = useState(0.6);
    
    const currentStaminaRef = useRef(currentStamina);
    const previewCostRef = useRef(previewCost);

    useEffect(() => { currentStaminaRef.current = currentStamina; }, [currentStamina]);
    useEffect(() => { previewCostRef.current = previewCost; }, [previewCost]);
    const drainElementRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        
        const handleDrainStop = (data: { unitId: string, amount: number }) => {
            setFrozenPreview(0);
            setPredictedStamina(null);
            setIsDraining(false);
        }
        
        

        const handleDrainStart = (data: { unitId: string, amount: number }) => {
            if (data.unitId !== unitId) return;

            const percentOfTotal = data.amount / maxStamina;
            const calculatedDuration = Math.max(MIN_DURATION, percentOfTotal * BASE_DRAIN_SPEED);

            // звичайний батчований setState
            setDrainDuration(calculatedDuration);
            setPredictedStamina(Math.max(0, currentStaminaRef.current - data.amount));
            setFrozenPreview(previewCostRef.current);
            setAnimatedDrain(data.amount);
            setIsDraining(true);

            // після наступного paint — перезапускаємо анімацію напряму
            requestAnimationFrame(() => {
                if (!drainElementRef.current) return;
                drainElementRef.current.style.animation = 'none';
                void drainElementRef.current.offsetWidth; // reflow
                drainElementRef.current.style.animation = 
                    `drainScale ${calculatedDuration}s ease-out forwards, staminaDrainPulse 1.8s ease-in-out infinite`;
            });
        };

        BattleBus.on('STAMINA_DRAIN_START', handleDrainStart);
        BattleBus.on('STAMINA_DRAIN_STOP', handleDrainStop);
        return () => {
            BattleBus.off('STAMINA_DRAIN_START', handleDrainStart);
            BattleBus.off('STAMINA_DRAIN_STOP', handleDrainStop);
        }
    }, [unitId]); // тільки unitId
    
    const effectiveStamina = predictedStamina !== null ? predictedStamina : currentStamina;
    const currentPercent = maxStamina > 0 ? (effectiveStamina / maxStamina) * 100 : 0;
    const drainPercent = maxStamina > 0 ? (animatedDrain / maxStamina) * 100 : 0;
    
    const activePreviewCost = frozenPreview > 0 ? frozenPreview : previewCost;
    const previewPercent = maxStamina > 0 ? (activePreviewCost / maxStamina) * 100 : 0;

    

    // console.log('[StaminaCore] Rendering with:', {
    //   predictedStamina,
    //     currentStamina,
    //     maxStamina,
    //     animatedDrain,
    //     effectiveStamina,
    //     activePreviewCost,
    //     currentPercent,
    //     previewPercent,
    //     drainPercent,
    //     isDraining
    // });
    
    return (
        <div className="unit-name-stamina">

            
            <div className="stamina-bar-container">
                <StaminaBar
                    currentPercent={currentPercent}
                    drainWidthPercent={drainPercent}
                    previewWidthPercent={previewPercent}
                    isDraining={isDraining}
                    drainDuration={drainDuration}
                    drainRef={drainElementRef}
                    height={20}
                />
            </div>
            
            <div className="stamina-values">
                <span className="current">{effectiveStamina}</span>
                <span className="separator">/</span>
                <span className="max">{maxStamina}</span>
                <span className="preview">{previewCost > 0 ? ` >> ${Math.max(currentStamina - previewCost, 0)}` : ''}</span>
            </div>
        </div>
    );
});